// src/pages/school/Exam/ExamTemplate.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Card, Row, Col, Form, Button, Badge, Alert, Spinner, ProgressBar, Dropdown,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";
import { collection, doc, setDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { getAuth } from "firebase/auth";
import "./exam.css";

/* -------------------- Helpers & small components -------------------- */

// Checkbox multi-select dropdown (with search + select all)
const MultiSelectDropdown = ({
  options = [],            // [{value,label}]
  value = [],              // string[]
  onChange,
  disabled = false,
  placeholder = "Select…",
  size = "md",
  menuMaxHeight = 320,
  className = "",
}) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const selectAllRef = React.useRef(null);

  const filtered = options.filter((o) =>
    (o.label || "").toLowerCase().includes(q.toLowerCase())
  );

  const allSelected = value.length === options.length && options.length > 0;
  const someSelected = value.length > 0 && !allSelected;

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const toggleOne = (val) =>
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);

  const selectAll = () => onChange(options.map((o) => o.value));
  const clearAll = () => onChange([]);

  const labelText = value.length ? `${value.length} selected` : placeholder;

  return (
    <Dropdown
      show={open}
      onToggle={setOpen}
      className={`w-100 ${className}`}
      align="start"
      drop="down"
      autoClose="outside"
      renderMenuOnMount
    >
      <Dropdown.Toggle
        variant="outline-secondary"
        className="w-100 text-start"
        disabled={disabled}
        size={size}
      >
        {labelText}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="w-100 p-2 shadow-sm"
        style={{ maxHeight: menuMaxHeight, overflowY: "auto", zIndex: 2000, position: "absolute" }}
      >
        <Form.Control
          size="sm"
          placeholder="Search…"
          className="mb-2"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="d-flex justify-content-between align-items-center px-1 pb-2 border-bottom mb-2">
          <Form.Check
            type="checkbox"
            id="ms-select-all"
            label="Select all"
            ref={selectAllRef}
            checked={allSelected}
            onChange={() => (allSelected ? clearAll() : selectAll())}
          />
          <Button size="sm" variant="link" onClick={clearAll}>Clear</Button>
        </div>

        {filtered.map((opt) => (
          <div key={opt.value} className="px-1 rounded ms-opt">
            <Form.Check
              type="checkbox"
              id={`ms-${opt.value}`}
              className="px-2 py-2"
              label={opt.label}
              checked={value.includes(opt.value)}
              onChange={() => toggleOne(opt.value)}
            />
          </div>
        ))}
        {!filtered.length && <div className="text-muted small px-2 py-1">No matches</div>}
      </Dropdown.Menu>
    </Dropdown>
  );
};

/* -------------------- Class, Section & Branch option lists -------------------- */
const CLASS_OPTIONS = [
  // School classes
  ...Array.from({ length: 12 }, (_, i) => ({
    value: `Class ${i + 1}`,
    label: `Class ${i + 1}`,
  })),

  // Class 12 specialization streams
  { value: "Class 12 PCM", label: "Class 12 PCM" },
  { value: "Class 12 PCB", label: "Class 12 PCB" },
  { value: "Class 12 PCMB", label: "Class 12 PCMB" },
  { value: "Class 12 Commerce", label: "Class 12 Commerce" },
  { value: "Class 12 Arts", label: "Class 12 Arts" },

  // Foundation
  { value: "Foundation 6", label: "Foundation 6" },
  { value: "Foundation 7", label: "Foundation 7" },
  { value: "Foundation 8", label: "Foundation 8" },
  { value: "Foundation 9", label: "Foundation 9" },
  { value: "Foundation 10", label: "Foundation 10" },

  // Competitive prep
  { value: "NEET", label: "NEET" },
  { value: "JEE", label: "JEE" },
  { value: "Class 11 NEET", label: "Class 11 NEET" },
  { value: "Class 11 JEE", label: "Class 11 JEE" },
  { value: "Class 12 NEET", label: "Class 12 NEET" },
  { value: "Class 12 JEE", label: "Class 12 JEE" },

  // Repeaters / Short-term
  { value: "LT", label: "LT" },
  { value: "NEET Repeaters", label: "NEET Repeaters" },
  { value: "JEE Repeaters", label: "JEE Repeaters" },
  { value: "Crash Course", label: "Crash Course" },
  { value: "Integrated", label: "Integrated" },

  // Undergraduate (UG)
  { value: "B.Tech", label: "B.Tech" },
  { value: "BE", label: "BE" },
  { value: "B.Sc", label: "B.Sc" },
  { value: "B.Com", label: "B.Com" },
  { value: "BA", label: "BA" },
  { value: "BBA", label: "BBA" },
  { value: "BCA", label: "BCA" },

  // Medical / Allied Health
  { value: "MBBS", label: "MBBS" },
  { value: "BDS", label: "BDS" },
  { value: "Pharmacy", label: "Pharmacy" },
  { value: "Nursing", label: "Nursing" },

  // Vocational
  { value: "Diploma / Polytechnic", label: "Diploma / Polytechnic" },
  { value: "ITI", label: "ITI" },

  // Misc
  { value: "Other Higher Education", label: "Other Higher Education" },
];

// A–Z as plain letters (no "Section " prefix)
const SECTION_OPTIONS = Array.from({ length: 26 }, (_, i) => {
  const k = String.fromCharCode(65 + i); // A..Z
  return { value: k, label: k };
});

/* ---------- Common base branch options ---------- */
const BRANCH_OPTIONS_BASE = [
  { value: "Science", label: "Science" },
  { value: "Commerce", label: "Commerce" },
  { value: "Arts", label: "Arts" },
  { value: "CSE", label: "CSE" },
  { value: "ECE", label: "ECE" },
  { value: "EEE", label: "EEE" },
  { value: "MECH", label: "Mechanical" },
  { value: "CIVIL", label: "Civil" },
];

/* ---------- Context-aware branch options by class ---------- */
const branchOptionsFor = (className) => {
  if (["B.Tech", "BE", "Diploma / Polytechnic"].includes(className)) {
    return [
      { value: "CSE", label: "CSE" },
      { value: "IT", label: "IT" },
      { value: "ECE", label: "ECE" },
      { value: "EEE", label: "EEE" },
      { value: "Mechanical", label: "Mechanical" },
      { value: "Civil", label: "Civil" },
      { value: "AI/ML", label: "AI / ML" },
      { value: "Data Science", label: "Data Science" },
      { value: "Biotech", label: "Biotechnology" },
      { value: "Chemical", label: "Chemical" },
      { value: "Mechatronics", label: "Mechatronics" },
      { value: "Automobile", label: "Automobile" },
      { value: "Other", label: "Other" },
    ];
  }
  if (className === "B.Sc") {
    return [
      { value: "Physics", label: "Physics" },
      { value: "Chemistry", label: "Chemistry" },
      { value: "Mathematics", label: "Mathematics" },
      { value: "Computer Science", label: "Computer Science" },
      { value: "Biotechnology", label: "Biotechnology" },
      { value: "Zoology", label: "Zoology" },
      { value: "Botany", label: "Botany" },
      { value: "Statistics", label: "Statistics" },
      { value: "Other", label: "Other" },
    ];
  }
  if (className === "B.Com") {
    return [
      { value: "General", label: "General" },
      { value: "Accounting & Finance", label: "Accounting & Finance" },
      { value: "Banking & Insurance", label: "Banking & Insurance" },
      { value: "Computer Applications", label: "Computer Applications" },
      { value: "Corporate Secretaryship", label: "Corporate Secretaryship" },
      { value: "Other", label: "Other" },
    ];
  }
  if (className === "BA") {
    return [
      { value: "English", label: "English" },
      { value: "Economics", label: "Economics" },
      { value: "History", label: "History" },
      { value: "Political Science", label: "Political Science" },
      { value: "Sociology", label: "Sociology" },
      { value: "Psychology", label: "Psychology" },
      { value: "Tamil", label: "Tamil" },
      { value: "Other", label: "Other" },
    ];
  }
  if (className === "BBA") {
    return [
      { value: "General", label: "General" },
      { value: "Finance", label: "Finance" },
      { value: "Marketing", label: "Marketing" },
      { value: "HR", label: "HR" },
      { value: "Business Analytics", label: "Business Analytics" },
      { value: "Logistics", label: "Logistics" },
      { value: "Other", label: "Other" },
    ];
  }
  if (className === "BCA") {
    return [
      { value: "General", label: "General" },
      { value: "Data Science", label: "Data Science" },
      { value: "AI/ML", label: "AI / ML" },
      { value: "Cloud Computing", label: "Cloud Computing" },
      { value: "Cyber Security", label: "Cyber Security" },
      { value: "Other", label: "Other" },
    ];
  }
  if (["Class 12 PCM", "Class 12 PCB", "Class 12 PCMB", "Class 12 Commerce", "Class 12 Arts"].includes(className)) {
    return [
      { value: "PCM", label: "PCM" },
      { value: "PCB", label: "PCB" },
      { value: "PCMB", label: "PCMB" },
      { value: "Commerce", label: "Commerce" },
      { value: "Arts", label: "Arts" },
    ];
  }
  return null; // free-text fallback
};

/* ---------- Test Type options (category) ---------- */
export const TEST_TYPE_OPTIONS = [
  // Cadence
  { value: "daily_test",         label: "Daily Test" },
  { value: "weekly_test",        label: "Weekly Test" },
  { value: "fortnightly_test",   label: "Fortnightly Test" },
  { value: "monthly_test",       label: "Monthly Test" },

  // Syllabus scope
  { value: "chapter_wise",       label: "Chapter-wise Test" },
  { value: "topic_wise_test",    label: "Topic-wise Test" },
  { value: "unit_test",          label: "Unit Test" },
  { value: "sectional_test",     label: "Sectional Test" },
  { value: "part_test",          label: "Part Test" },
  { value: "full_syllabus_test", label: "Full Syllabus Test" },
  { value: "cumulative",         label: "Cumulative" },

  // Academic terms
  { value: "midterm_exam",       label: "Midterm Exam" },
  { value: "quarterly_exam",     label: "Quarterly Exam" },
  { value: "half_yearly_exam",   label: "Half-Yearly Exam" },
  { value: "preboard_exam",      label: "Pre-Board Exam" },
  { value: "final_exam",         label: "Final/End-Term Exam" },

  // Practice & diagnostics
  { value: "diagnostic_test",    label: "Diagnostic/Baseline Test" },
  { value: "practice_test",      label: "Practice Test" },
  { value: "speed_test",         label: "Speed Test" },
  { value: "revision_test",      label: "Revision Test" },
  { value: "recap_quiz",         label: "Recap Quiz" },
  { value: "remedial_test",      label: "Remedial Test" },

  // Papers
  { value: "sample_paper",       label: "Sample Paper" },
  { value: "previous_year_paper",label: "Previous Year Paper" },

  // Competitive-specific
  { value: "mock_test",          label: "Mock Test" },
  { value: "mini_mock",          label: "Mini Mock" },
  { value: "mega_mock",          label: "Mega Mock" },
  { value: "neet_sectional",     label: "NEET Sectional" },
  { value: "neet_full_syllabus", label: "NEET Full Syllabus" },
  { value: "jee_sectional",      label: "JEE Sectional" },
  { value: "jee_full_syllabus",  label: "JEE Full Syllabus" },

  // Formats & others
  { value: "surprise_test",      label: "Surprise Test" },
  { value: "open_book_test",     label: "Open-Book Test" },
  { value: "oral_viva",          label: "Oral/Viva" },
  { value: "practical_assessment", label: "Practical/Lab Assessment" },
  { value: "online_proctored",   label: "Online Proctored Test" },
  { value: "take_home_test",     label: "Take-home Test" },
  { value: "assignment_based",   label: "Assignment-based Assessment" },
  { value: "bridge_course_test", label: "Bridge Course Test" },
  { value: "aptitude_test",      label: "Aptitude Test" },
  { value: "reasoning_test",     label: "Reasoning Test" },
  { value: "scholarship_test",   label: "Scholarship/NMMS Test" },

  // Custom
  { value: "other",              label: "Other… (custom)" },
];

// For text display
const testTypeLabel = (value, custom) => {
  if (value === "other" && custom?.trim()) return custom.trim();
  return TEST_TYPE_OPTIONS.find((o) => o.value === value)?.label || "Daily Test";
};

/* ---------- Sections helpers for subject distribution ---------- */
const DEFAULT_SECTION = (key = "A") => ({
  id: uuid(),
  key,
  label: key,
   questionCount: 0,
  marksPerCorrect: 1,   // ✅ NEW
  negativeMarks: 0, 
});

const nextSectionKey = (sections) => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const used = new Set((sections || []).map((s) => (s.key || "").toUpperCase()));
  for (let i = 0; i < letters.length; i++) {
    const k = letters[i];
    if (!used.has(k)) return k;
  }
  return String((sections?.length || 0) + 1);
};

const DEFAULT_SUBJECT_ROW = () => ({
  id: uuid(),
  subjectId: "",
  subjectName: "",
  sections: [DEFAULT_SECTION("A")],
});

/* ---------- Default form ---------- */
const DEFAULT_FORM = {
  name: "",
  examType: "objective",         // 'objective' | 'descriptive'

  // UI field "Test Type" (stored as testType/testTypeLabel/testTypeCustom)
  testType: "daily_test",
  testTypeCustom: "",

  totalQuestions: 0,
  subjectWise: [DEFAULT_SUBJECT_ROW()],
  status: "draft", // "draft" | "ready"

  // Optional defaults (refined at scheduling)
  classIds: [],
  sectionIds: [],
  branchIds: [],

  // Behavior flags (derived at save, but surfaced for edit/load safety)
  onlineAttemptEnabled: true,     // objective -> true, descriptive -> false
  requiresPdfUpload: false,       // descriptive -> true
  pdfMaxSizeMB: 5,                // only for descriptive
  pdfInstructions: "",            // only for descriptive

  // UI/Attempt toggles
  randomizeQuestions: true,
  allowBackNavigation: true,
  showMarksPerQuestion: true,     // display only
  allowedDevices: ["web"],
  instructions: "",
  visibility: "draft",
};

export default function ExamTemplate() {
  const { id: templateId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const templateSeed = location.state?.templateSeed || null;

  const { schoolId, school, isActive: isActiveSchool, loading: schoolLoading } = useSchool();
  const auth = getAuth();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(Boolean(templateId && templateId !== "new" && !templateSeed));

  const isEdit = Boolean(templateId && templateId !== "new");

  /* ---------- Derived sums ---------- */
  const subjectSum = useMemo(() => {
    return (form.subjectWise || []).reduce((acc, subj) => {
      const secSum = (subj.sections || []).reduce((a, s) => a + (+s.questionCount || 0), 0);
      return acc + secSum;
    }, 0);
  }, [form.subjectWise]);

  const ratio =
    +form.totalQuestions > 0
      ? Math.min(100, Math.round((subjectSum / +form.totalQuestions) * 100))
      : 0;

  const canSave = useMemo(() => {
    if (!isActiveSchool) return false;
    if (!form.name?.trim()) return false;
    if (!(Number.isFinite(+form.totalQuestions) && +form.totalQuestions > 0)) return false;
    if (subjectSum !== +form.totalQuestions) return false;
    return true;
  }, [form, subjectSum, isActiveSchool]);

  /* ---------- Utils ---------- */
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleChangeBasic = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      update({ [name]: checked });
    } else if (["totalQuestions"].includes(name)) {
      update({ [name]: value.replace(/[^0-9]/g, "") });
    } else {
      update({ [name]: value });
    }
  };

  /* ---------- Subject & Section helpers (distribution) ---------- */
  const addSubjectRow = () => {
    setForm((f) => ({
      ...f,
      subjectWise: [...(f.subjectWise || []), DEFAULT_SUBJECT_ROW()],
    }));
  };
  const removeSubjectRow = (id) => {
    setForm((f) => ({
      ...f,
      subjectWise: (f.subjectWise || []).filter((r) => r.id !== id),
    }));
  };
  const updateSubjectRow = (id, patch) => {
    setForm((f) => ({
      ...f,
      subjectWise: (f.subjectWise || []).map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  };

  const addSection = (subjectId) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const key = nextSectionKey(s.sections || []);
        return { ...s, sections: [...(s.sections || []), DEFAULT_SECTION(key)] };
      });
      return { ...f, subjectWise };
    });
  };

  const removeSection = (subjectId, sectionId) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const next = (s.sections || []).filter((sec) => sec.id !== sectionId);
        return { ...s, sections: next.length ? next : [DEFAULT_SECTION("A")] };
      });
      return { ...f, subjectWise };
    });
  };

  const updateSection = (subjectId, sectionId, patch) => {
    setForm((f) => {
      const subjectWise = (f.subjectWise || []).map((s) => {
        if (s.id !== subjectId) return s;
        const nextSecs = (s.sections || []).map((sec) =>
          sec.id === sectionId ? { ...sec, ...patch } : sec
        );
        return { ...s, sections: nextSecs };
      });
      return { ...f, subjectWise };
    });
  };

  const resetForm = () => setForm(DEFAULT_FORM);

  /* -------------------- Inflate/Deflate -------------------- */
  const normalizeDocToForm = (docData) => {
    if (!docData) return DEFAULT_FORM;

    const safeSections = (secs) =>
      (secs || []).map((sec) => ({
        id: sec.id || uuid(),
        key: (sec.key || "A").toUpperCase(),
        label: sec.label || (sec.key || "A"),
        questionCount: +sec.questionCount || 0,
     marksPerCorrect: Number.isFinite(+sec.marksPerCorrect) ? +sec.marksPerCorrect : 1,
    negativeMarks: Number.isFinite(+sec.negativeMarks) ? +sec.negativeMarks : 0,
      }));

    const safeSubjects = (arr) =>
      (arr || []).map((s) => ({
        id: uuid(), // UI-only id
        subjectId: String(s.subjectId || "").trim(),
        subjectName: String(s.subjectName || "").trim(),
        sections: safeSections(s.sections),
      }));

    // Read new fields first; fall back to legacy examMode fields
    const docTestType = docData.testType || docData.examMode || "daily_test";
    const docTestTypeCustom = docData.testTypeCustom || docData.examModeCustom || "";
    const docTestTypeLabel = docData.testTypeLabel || docData.examModeLabel || testTypeLabel(docTestType, docTestTypeCustom);

    return {
      ...DEFAULT_FORM,
      name: docData.name || "",
      examType: (docData.examType === "descriptive") ? "descriptive" : "objective",
      testType: docTestType,
      testTypeCustom: docTestTypeCustom,
      // label is computed at save/display; not needed in form state
      totalQuestions: Number.isFinite(+docData.totalQuestions) ? +docData.totalQuestions : 0,
      subjectWise: safeSubjects(docData.subjectWise),
      classIds: Array.isArray(docData.classIds) ? docData.classIds : [],
      sectionIds: Array.isArray(docData.sectionIds) ? docData.sectionIds.map(String) : [],
      branchIds: Array.isArray(docData.branchIds) ? docData.branchIds.map(String) : [],
      randomizeQuestions: !!docData.randomizeQuestions,
      allowBackNavigation: !!docData.allowBackNavigation,
      showMarksPerQuestion: !!docData.showMarksPerQuestion,
      allowedDevices: Array.isArray(docData.allowedDevices) ? docData.allowedDevices : ["web"],
      instructions: docData.instructions || "",
      visibility: docData.visibility || "draft",
      status: docData.status || "draft",
      onlineAttemptEnabled: !!docData.onlineAttemptEnabled,
      requiresPdfUpload: !!docData.requiresPdfUpload,
      pdfMaxSizeMB: Number.isFinite(+docData.pdfMaxSizeMB) ? +docData.pdfMaxSizeMB : 5,
      pdfInstructions: docData.pdfInstructions || "",
      // Preserve label usage in UI footer via computed helper if needed
      _loadedTestTypeLabel: docTestTypeLabel,
    };
  };

  const buildPayloadForSave = (idToUse) => {
    const normalizedSubjects = (form.subjectWise || []).map((s) => {
      const sections = (s.sections || []).map((sec) => ({
        id: sec.id,
        key: (sec.key || "").toUpperCase(),
        label: (sec.label || (sec.key || "")),
        questionCount: +sec.questionCount || 0,
         marksPerCorrect: Number.isFinite(+sec.marksPerCorrect) ? +sec.marksPerCorrect : 1,
  negativeMarks: Number.isFinite(+sec.negativeMarks) ? +sec.negativeMarks : 0,
      }));
      const perSubjectTotal = sections.reduce((a, sec) => a + (sec.questionCount || 0), 0);
      return {
        subjectId: String(s.subjectId || "").trim(),
        subjectName: String(s.subjectName || "").trim(),
        questionCount: perSubjectTotal,
        sections,
      };
    });

    const grandTotal = normalizedSubjects.reduce((a, s) => a + (s.questionCount || 0), 0);

    // Default per-class mapping for sections & branches (applies selected IDs to every class)
    const defaultSectionMap = (form.classIds || []).reduce((acc, cid) => {
      acc[cid] = (form.sectionIds || []).map((k) => String(k).toUpperCase());
      return acc;
    }, {});
    const defaultBranchMap = (form.classIds || []).reduce((acc, cid) => {
      acc[cid] = (form.branchIds || []).map((k) => String(k).toUpperCase());
      return acc;
    }, {});

    // derive behavior from examType
    const isDescriptive = form.examType === "descriptive";
    const onlineAttemptEnabled = !isDescriptive; // objective => true
    const requiresPdfUpload = isDescriptive;     // descriptive => true

    const labelForType = testTypeLabel(form.testType, form.testTypeCustom);

    return {
      id: idToUse,
      name: form.name.trim(),
      examType: isDescriptive ? "descriptive" : "objective",

      // ✅ Store as TEST TYPE (new schema)
      testType: form.testType, // e.g., 'daily_test'
      testTypeLabel: labelForType,
      testTypeCustom: form.testType === "other" ? (form.testTypeCustom || "") : "",

      totalQuestions: +form.totalQuestions,
      subjectWise: normalizedSubjects,
      totalSubjectQuestions: grandTotal,

      // Defaults used by the scheduler; refined per class later
      classIds: form.classIds,
      sectionIds: (form.sectionIds || []).map((k) => String(k).toUpperCase()),
      branchIds: (form.branchIds || []).map((k) => String(k).toUpperCase()),
      sectionKeysByClass: defaultSectionMap,
      branchKeysByClass: defaultBranchMap,

      // student behavior flags
      onlineAttemptEnabled,
      requiresPdfUpload,
      pdfMaxSizeMB: isDescriptive ? (+form.pdfMaxSizeMB || 5) : null,
      pdfInstructions: isDescriptive ? (form.pdfInstructions || "") : "",

      randomizeQuestions: !!form.randomizeQuestions,
      allowBackNavigation: !!form.allowBackNavigation,
      showMarksPerQuestion: !!form.showMarksPerQuestion,
      allowedDevices: form.allowedDevices || ["web"],
      instructions: form.instructions || "",
      visibility: form.visibility || "draft",
      status: form.status || "draft",
      createdBy: auth.currentUser?.uid || "unknown",
      updatedAt: serverTimestamp(),
      ...(isEdit ? {} : { createdAt: serverTimestamp() }),
    };
  };

  /* -------------------- Load (Edit) -------------------- */
  useEffect(() => {
    if (templateSeed && isEdit) {
      setForm(normalizeDocToForm(templateSeed));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateSeed, isEdit]);

  useEffect(() => {
    if (!schoolId || !isEdit) return;

    setLoadingDoc(!templateSeed);
    const ref = doc(db, "schools", schoolId, "examTemplates", templateId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          setForm(normalizeDocToForm(data));
        }
        setLoadingDoc(false);
      },
      () => setLoadingDoc(false)
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, templateId, isEdit]);

  useEffect(() => {
    if (!isEdit) {
      setForm(DEFAULT_FORM);
      setLoadingDoc(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  /* -------------------------- Dynamic branches -------------------------- */

  const dynamicBranchOptions = useMemo(() => {
    const map = new Map();
    (form.classIds || []).forEach((cid) => {
      const opts = branchOptionsFor(cid) || [];
      opts.forEach((o) => {
        if (!map.has(o.value)) map.set(o.value, o);
      });
    });
    return Array.from(map.values());
  }, [form.classIds]);

  /* -------------------------- Save -------------------------- */
  const doSave = async (goToQuestionsAfter = false) => {
    if (!schoolId) {
      await Swal.fire("School not selected", "Please select a school context.", "warning");
      return;
    }
    if (!canSave) {
      await Swal.fire("Check fields", "Complete required fields and fix validation errors.", "warning");
      return;
    }

    try {
      setSaving(true);

      const docRef = isEdit
        ? doc(db, "schools", schoolId, "examTemplates", templateId)
        : doc(collection(db, "schools", schoolId, "examTemplates"));

      const payload = buildPayloadForSave(docRef.id);
      await setDoc(docRef, payload, { merge: true });

      if (goToQuestionsAfter) {
        nav(`/school/exams/templates/${docRef.id}/questions`);
        return;
      }

      await Swal.fire({
        icon: "success",
        title: isEdit ? "Template updated" : "Template created",
        text: "Saved successfully.",
        confirmButtonText: "OK",
      });

      if (!isEdit) setForm(DEFAULT_FORM);
    } catch (e) {
      console.error(e);
      await Swal.fire("Error", e?.message || "Failed to save template.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------ UI ------------------------------ */
  if (schoolLoading || loadingDoc) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner />
      </div>
    );
  }

  // Merge class options with any saved values not in default list
  const extraClassIds = (form.classIds || []).filter(
    (c) => !CLASS_OPTIONS.some((opt) => opt.value === c)
  );
  const ALL_CLASS_OPTIONS = [
    ...CLASS_OPTIONS,
    ...extraClassIds.map((c) => ({ value: c, label: c })),
  ];

  // Merge dynamic + base with any saved custom branch IDs
  const extraBranchIds = (form.branchIds || []).filter(
    (b) =>
      !BRANCH_OPTIONS_BASE.some((opt) => opt.value === b) &&
      !dynamicBranchOptions.some((opt) => opt.value === b)
  );
  const ALL_BRANCH_OPTIONS = [
    ...dynamicBranchOptions,
    ...BRANCH_OPTIONS_BASE,
    ...extraBranchIds.map((b) => ({ value: b, label: b })),
  ];

  const disabled = !isActiveSchool || saving;
  const isDescriptive = form.examType === "descriptive";

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="fw-semibold">
          {isEdit ? "Edit Exam Template" : "Create Exam Template"}{" "}
          <Badge bg="light" text="dark">School: {school?.name || "-"}</Badge>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Badge bg={isActiveSchool ? "success" : "secondary"}>
            {isActiveSchool ? "School Active" : "School Inactive"}
          </Badge>
          <Badge bg={String(form.status).toLowerCase() === "ready" ? "success" : "secondary"}>
            {String(form.status || "draft")}
          </Badge>
        </div>
      </Card.Header>

      <Card.Body>
        {!isActiveSchool && (
          <Alert variant="warning" className="mb-4">
            Your school is not <strong>active</strong>. Activate the school to create or edit templates.
          </Alert>
        )}

        {/* Top summary / progress */}
        <Row className="g-3 mb-2">
          <Col md={8}>
            <div className="p-3 border rounded-3 bg-light">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div className="fw-semibold">Question Allocation</div>
                <small className="text-muted">{subjectSum} of {+form.totalQuestions || 0}</small>
              </div>
              <ProgressBar now={ratio} label={`${ratio}%`} variant={ratio === 100 ? "success" : "info"} />
            </div>
          </Col>
          <Col md={4} className="d-flex align-items-center">
            <Form.Check
              type="switch"
              id="mark-ready"
              label="Mark template as Ready"
              disabled={disabled}
              checked={String(form.status).toLowerCase() === "ready"}
              onChange={(e) => update({ status: e.target.checked ? "ready" : "draft" })}
            />
          </Col>
        </Row>

        <Row className="g-3">
          {/* Basics */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>Exam Name</Form.Label>
              <Form.Control
                disabled={disabled}
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="Mid-Term – Mathematics"
              />
            </Form.Group>
          </Col>

          {/* Exam Type */}
          <Col md={3}>
            <Form.Group>
              <Form.Label>Exam Type</Form.Label>
              <Form.Select
                disabled={disabled}
                value={form.examType}
                onChange={(e) => {
                  const nextType = e.target.value === "descriptive" ? "descriptive" : "objective";
                  update({
                    examType: nextType,
                    onlineAttemptEnabled: nextType !== "descriptive",
                    requiresPdfUpload: nextType === "descriptive",
                  });
                }}
              >
                <option value="objective">Objective (online attempt)</option>
                <option value="descriptive">Descriptive (PDF answer upload)</option>
              </Form.Select>
              <div className="mt-2">
                {isDescriptive ? (
                  <Badge bg="warning" text="dark">PDF upload required</Badge>
                ) : (
                  <Badge bg="success">Online attempt</Badge>
                )}
              </div>
            </Form.Group>
          </Col>

          {/* NEW: Test Type */}
          <Col md={3}>
            <Form.Group>
              <Form.Label>Test Type</Form.Label>
              <Form.Select
                disabled={disabled}
                value={form.testType}
                onChange={(e) => update({ testType: e.target.value })}
              >
                {TEST_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
              {form.testType === "other" && (
                <Form.Control
                  className="mt-2"
                  placeholder="Enter custom test type (e.g., 'Sunday Test')"
                  value={form.testTypeCustom}
                  onChange={(e) => update({ testTypeCustom: e.target.value })}
                  disabled={disabled}
                />
              )}
              <Form.Text muted>
                Examples: Daily, Weekly, Grand, Cumulative, Mock, etc.
              </Form.Text>
            </Form.Group>
          </Col>

          {/* Default scope (optional at template stage) */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>Applicable Classes (optional)</Form.Label>
              <MultiSelectDropdown
                className="ms-dropdown"
                options={ALL_CLASS_OPTIONS}
                value={form.classIds}
                disabled={disabled}
                placeholder="Select classes"
                onChange={(next) => update({ classIds: next })}
              />
              <Form.Text muted>Pick any; you can refine when scheduling.</Form.Text>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Applicable Sections (optional)</Form.Label>
              <MultiSelectDropdown
                className="ms-dropdown"
                options={SECTION_OPTIONS}
                value={form.sectionIds}
                disabled={disabled}
                placeholder="Select sections (A, B, …)"
                onChange={(next) => update({ sectionIds: next })}
              />
              <Form.Text muted>Defaults only; actual class sections are confirmed during scheduling.</Form.Text>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Applicable Branches (optional)</Form.Label>
              <MultiSelectDropdown
                className="ms-dropdown"
                options={ALL_BRANCH_OPTIONS}
                value={form.branchIds}
                disabled={disabled}
                placeholder="Select branches (e.g., CSE, Science)"
                onChange={(next) => update({ branchIds: next })}
              />
              <Form.Text muted>
                {dynamicBranchOptions.length
                  ? "Suggested by selected classes; you can add custom too."
                  : "Defaults only; actual class branches are confirmed during scheduling."}
              </Form.Text>
            </Form.Group>
          </Col>

          {/* Total questions with live sum check */}
          <Col md={3}>
            <Form.Group>
              <Form.Label>Total Questions</Form.Label>
              <Form.Control
                disabled={disabled}
                inputMode="numeric"
                value={form.totalQuestions}
                name="totalQuestions"
                onChange={handleChangeBasic}
              />
              <Form.Text muted>
                Sections sum: <strong>{subjectSum}</strong>{" "}
                {subjectSum !== +form.totalQuestions && (
                  <Badge bg="warning" text="dark" className="ms-2">Mismatch</Badge>
                )}
              </Form.Text>
            </Form.Group>
          </Col>

          {/* Descriptive-only PDF settings */}
          {isDescriptive && (
            <>
              <Col xs={12}><hr className="my-2" /></Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Max PDF Size (MB)</Form.Label>
                  <Form.Control
                    disabled={disabled}
                    inputMode="numeric"
                    value={form.pdfMaxSizeMB}
                    onChange={(e) =>
                      update({ pdfMaxSizeMB: e.target.value.replace(/[^0-9]/g, "") })
                    }
                    placeholder="5"
                  />
                  <Form.Text muted>Students will upload a single PDF answer sheet.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={9}>
                <Form.Group>
                  <Form.Label>PDF Upload Instructions (shown to students)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    disabled={disabled}
                    value={form.pdfInstructions}
                    onChange={(e) => update({ pdfInstructions: e.target.value })}
                    placeholder="Scan in order, ensure all pages are readable, merge into a single PDF, and upload."
                  />
                </Form.Group>
              </Col>
            </>
          )}

          <Col xs={12}><hr /></Col>

          {/* ---------- Subject-wise Distribution with Sections ---------- */}
          <Col xs={12}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="m-0 fw-semibold">Subject-wise Distribution</Form.Label>
              <Button disabled={disabled} size="sm" variant="outline-primary" onClick={addSubjectRow}>
                + Add Subject
              </Button>
            </div>

            {form.subjectWise.map((row) => {
              const perSubjectTotal = (row.sections || []).reduce((a, s) => a + (+s.questionCount || 0), 0);
              return (
                <Card key={row.id} className="mb-3 shadow-sm">
                  <Card.Header className="d-flex align-items-center gap-2">
                    <div className="flex-grow-1">
                      <div className="small text-muted">Subject</div>
                      <div className="fw-semibold">{row.subjectName || "Untitled"}</div>
                    </div>
                    <Badge bg="light" text="dark">Subject Total: {perSubjectTotal}</Badge>
                    {form.subjectWise.length > 1 && (
                      <Button
                        disabled={disabled}
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeSubjectRow(row.id)}
                        title="Remove subject"
                      >
                        ✕
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-2 align-items-end">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Subject Name</Form.Label>
                          <Form.Control
                            disabled={disabled}
                            value={row.subjectName}
                            onChange={(e) => updateSubjectRow(row.id, { subjectName: e.target.value })}
                            placeholder="Physics"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small mb-1">Subject ID (optional)</Form.Label>
                          <Form.Control
                            disabled={disabled}
                            value={row.subjectId}
                            onChange={(e) => updateSubjectRow(row.id, { subjectId: e.target.value })}
                            placeholder="physics-12"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={2} className="d-flex align-items-end">
                        <Button
                          disabled={disabled}
                          size="sm"
                          variant="outline-success"
                          className="ms-auto"
                          onClick={() => addSection(row.id)}
                        >
                          + Add Section
                        </Button>
                      </Col>

                      <Col xs={12}><hr className="my-2" /></Col>

                     {(row.sections || []).map((sec) => (
  <Row key={sec.id} className="g-2 align-items-end mb-2">
    {/* 2 + 3 + 2 + 2 + 2 + 1 = 12 */}
    <Col md={2}>
      <Form.Group>
        <Form.Label className="small mb-1">Key</Form.Label>
        <Form.Control
          disabled={disabled}
          value={sec.key}
          onChange={(e) => {
            const newKey = e.target.value.toUpperCase().slice(0, 3);
            const syncLabel =
              !sec.label || sec.label.toUpperCase() === String(sec.key || "").toUpperCase();
            updateSection(row.id, sec.id, {
              key: newKey,
              ...(syncLabel ? { label: newKey } : {}),
            });
          }}
          placeholder="A"
        />
      </Form.Group>
    </Col>

    <Col md={3}>
      <Form.Group>
        <Form.Label className="small mb-1">Label (optional)</Form.Label>
        <Form.Control
          disabled={disabled}
          value={sec.label}
          onChange={(e) => updateSection(row.id, sec.id, { label: e.target.value })}
          placeholder="A"
        />
      </Form.Group>
    </Col>

    <Col md={2}>
      <Form.Group>
        <Form.Label className="small mb-1">Questions</Form.Label>
        <Form.Control
          disabled={disabled}
          inputMode="numeric"
          value={sec.questionCount ?? ""}
          onChange={(e) =>
            updateSection(row.id, sec.id, {
              questionCount: e.target.value.replace(/[^0-9]/g, ""),
            })
          }
          placeholder="10"
        />
      </Form.Group>
    </Col>

    <Col md={2}>
      <Form.Group>
        <Form.Label className="small mb-1">Marks / Correct</Form.Label>
        <Form.Control
          disabled={disabled}
          inputMode="numeric"
          value={sec.marksPerCorrect ?? 1}
          onChange={(e) =>
            updateSection(row.id, sec.id, {
              // keep as string in UI; you coerce to number when saving
              marksPerCorrect: e.target.value.replace(/[^0-9.]/g, ""),
            })
          }
          placeholder="1"
        />
      </Form.Group>
    </Col>

    <Col md={2}>
      <Form.Group>
        <Form.Label className="small mb-1">Negative Marks</Form.Label>
        <Form.Control
          disabled={disabled}
          inputMode="numeric"
          value={sec.negativeMarks ?? 0}
          onChange={(e) =>
            updateSection(row.id, sec.id, {
              negativeMarks: e.target.value.replace(/[^0-9.]/g, ""),
            })
          }
          placeholder="0"
        />
      </Form.Group>
    </Col>

    <Col md={1} className="text-end">
      {(row.sections || []).length > 1 && (
        <Button
          disabled={disabled}
          variant="outline-danger"
          size="sm"
          onClick={() => removeSection(row.id, sec.id)}
          title="Remove section"
        >
          ✕
        </Button>
      )}
    </Col>
  </Row>
))}

                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </Col>

          <Col xs={12}><hr /></Col>

          {/* Behavior toggles */}
          <Col md={4}>
            <Form.Check
              type="switch"
              id="rand-qs"
              label="Randomize Questions"
              disabled={disabled || isDescriptive}
              checked={form.randomizeQuestions}
              onChange={(e) => update({ randomizeQuestions: e.target.checked })}
            />
          </Col>
          <Col md={4}>
            <Form.Check
              type="switch"
              id="back-nav"
              label="Allow Back Navigation"
              disabled={disabled}
              checked={form.allowBackNavigation}
              onChange={(e) => update({ allowBackNavigation: e.target.checked })}
            />
          </Col>
          <Col md={4}>
            <Form.Check
              type="switch"
              id="show-marks"
              label="Show Marks/Question (display only)"
              disabled={disabled}
              checked={form.showMarksPerQuestion}
              onChange={(e) => update({ showMarksPerQuestion: e.target.checked })}
            />
          </Col>

          <Col xs={12}>
            <Form.Group className="mt-2">
              <Form.Label>Instructions (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                disabled={disabled}
                value={form.instructions}
                onChange={(e) => update({ instructions: e.target.value })}
                placeholder="Read all questions carefully..."
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>

      {/* Sticky footer actions */}
      <div className="position-sticky bottom-0 bg-body border-top p-3">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Button variant="primary" disabled={!canSave || saving} onClick={() => doSave(false)}>
            {saving ? "Saving..." : isEdit ? "Update Template" : "Save Template"}
          </Button>

          <Button
            variant="success"
            disabled={!canSave || saving}
            onClick={() => doSave(true)}
            title={isEdit ? "Update and manage questions" : "Save and go add questions"}
          >
            {saving ? "Saving..." : isEdit ? "Update & Manage Questions" : "Save & Add Questions"}
          </Button>

          {!isEdit && (
            <Button variant="outline-secondary" disabled={saving} onClick={resetForm}>
              Reset
            </Button>
          )}

          <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
            <Badge bg={subjectSum === +form.totalQuestions && +form.totalQuestions > 0 ? "success" : "warning"}>
              Sections Sum: {subjectSum} / Total: {+form.totalQuestions || 0}
            </Badge>
            <Badge
              bg="info"
              title={form.testType === "other" ? form.testTypeCustom : testTypeLabel(form.testType)}
            >
              Test Type: {testTypeLabel(form.testType, form.testTypeCustom)}
            </Badge>
          </div>
        </div>
      </div>
    </Card>
  );
}
